<script>import { afterUpdate, onMount } from "svelte";
import { API } from "../../../api/api.js";
import {
  locale as globalLocale,
  timeZone as globalTimeZone,
  timeRange
} from "../global-store.js";
import { theme } from "../../../stores/theme.js";
import { toLocale } from "../../../i18n/translatable.js";
import { getDateTimeFormatters } from "../../d3/time-axis/timeline-scale.js";
import { humanTimeZone } from "../../util/i18n-time-util.js";
import Button from "../../../components/Button.svelte";
import SelectField from "../../../components/form/SelectField.svelte";
import TlmdDomainCaption from "../../TlmdDomainCaption.svelte";
import ReportConfigurator from "../../report/Configurator.svelte";
import * as XLSX from "xlsx";
import { isq } from "../../../isq/index.js";
import {
  getSelectedFeedIds,
  getSensorFeedProbeMap,
  processSensorData
} from "./sensor-export-helpers.js";
import FeedTableSection from "./FeedTableSection.svelte";
export let domain;
export let domainExtName;
export let timeZone = void 0;
export let locale = void 0;
export let rangeStart;
export let rangeEnd;
export let rangeType;
export let fixedRangeType = false;
export let host = void 0;
export let credentials = void 0;
export let assets = "";
export let feedTypeId;
export let feedName = void 0;
export let feedId = void 0;
let mounted = false;
let started = false;
let dataRange = void 0;
let headerElem;
let headerHeight = 0;
afterUpdate(() => {
  const height = headerElem ? headerElem.getBoundingClientRect().height : 0;
  if (height !== headerHeight) {
    headerHeight = height;
  }
});
$timeRange = { start: new Date(rangeStart), end: new Date(rangeEnd) };
$globalLocale = locale ?? $globalLocale;
$globalTimeZone = timeZone ?? $globalTimeZone;
$:
  reportConfiguration = {
    range: {
      start: $timeRange.start.getTime(),
      end: $timeRange.end.getTime(),
      type: rangeType
    }
  };
$:
  formatters = getDateTimeFormatters(
    $globalLocale,
    $globalTimeZone,
    "hours"
  );
$:
  startMarker = {
    millis: $timeRange.start.getTime(),
    label: formatters.bound($timeRange.start)
  };
$:
  endMarker = {
    millis: $timeRange.end.getTime(),
    label: formatters.bound($timeRange.end)
  };
$:
  rangeAsText = `${startMarker?.label ?? $timeRange.start} - ${endMarker?.label ?? $timeRange.end}`;
let feedTableProperty = void 0;
let sensorFeedType;
let sensorFeedProbes;
let selectedFeed = "__all__";
$: {
  if (reportConfiguration.range.end !== reportConfiguration.range.start && selectedFeed) {
    getData(reportConfiguration.range);
  }
}
$:
  exportTitle = getExportTitle(sensorFeedType);
onMount(async () => {
  mounted = true;
  console.log("Ready...");
  API.init(host ? host : "");
  API.setDomain(domain);
  if (credentials) {
    API.setCredentials(credentials.usr, credentials.pwd);
  } else {
    API.reloadCredentials();
  }
  await API.signIn();
  sensorFeedType = await API.getSensorFeedType(feedTypeId);
  sensorFeedProbes = await getSensorFeedProbeMap(sensorFeedType);
  started = true;
  await getData(reportConfiguration.range);
});
const openProperties = (key) => {
  const propertiesDiv = document.querySelector(".report div.properties");
  propertiesDiv.classList.remove("closed");
  let configuratorClassName = key;
  propertiesDiv.querySelectorAll("div.configurator").forEach((elem) => {
    if (elem.classList.contains(configuratorClassName)) {
      elem.classList.add("open");
    } else {
      elem.classList.remove("open");
    }
  });
};
const closeProperties = () => {
  const propertiesDiv = document.querySelector(".report div.properties");
  document.querySelector(".report div.properties")?.classList.add("closed");
};
const getExportTitle = (feedType) => {
  if (feedType) {
    let title = feedType.title;
    return title;
  }
  return "...";
};
const getData = async (range) => {
  const doGetData = started && range && range.start !== void 0 && range.end !== void 0 && (dataRange == void 0 || dataRange.start != range.start || dataRange.end != range.end);
  if (doGetData) {
    dataRange = range;
    let sensorFeeds = await API.getSensorFeeds(
      new Date(range.start).toISOString(),
      new Date(range.end).toISOString(),
      [feedTypeId]
    );
    const sensorFeedMap = sensorFeeds.reduce(
      (map, feed) => {
        map[feed.id] = feed;
        return map;
      },
      {}
    );
    const feedPatterns = getSelectedFeedIds(
      selectedFeed,
      sensorFeedMap,
      feedName,
      feedId
    );
    const sensorData = await API.getSensorEncodedData(
      new Date(range.start).toISOString(),
      new Date(range.end).toISOString(),
      feedPatterns
    );
    const feedSignalsMap = {};
    processSensorData(
      feedPatterns,
      sensorData,
      sensorFeedProbes,
      feedSignalsMap
    );
    feedTableProperty = {
      range,
      feeds: sensorFeedMap,
      signals: feedSignalsMap
    };
  }
};
let feedsDataTable;
const exportData = () => {
  let workbook = XLSX.utils.book_new();
  if (feedsDataTable) {
    let dataSheet = XLSX.utils.table_to_sheet(feedsDataTable);
    XLSX.utils.book_append_sheet(workbook, dataSheet, "Data", true);
  }
  XLSX.writeFile(workbook, `${exportTitle} Export - ${rangeAsText}.xlsx`);
};
</script>

<svelte:head>
  <title>{exportTitle} Export - {rangeAsText}</title>
</svelte:head>
{#if mounted && $globalLocale}
  <div class="report {$theme}" style="--headerHeight:{headerHeight}px;">
    <div class="content">
      <header bind:this={headerElem} class="header">
        <div class="caption">
          <div class="noprint float">
            <Button
              story={"button.primary.medium"}
              on:click={(ev) => exportData()}
              ><span class="icon-download" /></Button
            >
            <Button
              story={$theme === "dark"
                ? "button.secondary.medium.icon"
                : "button.primary.medium.icon"}
              on:click={(ev) => openProperties("general")}
              ><span class="glyph-cog" /></Button
            >
          </div>
          <TlmdDomainCaption {assets} domain={domainExtName} /> - {exportTitle} Export
        </div>
        <div class="info">
          {rangeAsText} ({humanTimeZone($globalTimeZone, $globalLocale)})
        </div>
        <div class="info">
          <!-- info here (e.g. number of data points)-->
        </div>
      </header>
      <div class="tlmd-scroll">
        {#if feedTableProperty !== undefined}
          <FeedTableSection
            bind:elem={feedsDataTable}
            data={feedTableProperty}
            probes={sensorFeedProbes}
          />
        {/if}
      </div>
    </div>

    <div class="noprint properties right closed">
      <div class="noprint float close">
        <Button
          story={$theme === "dark"
            ? "button.secondary.medium.icon"
            : "button.primary.medium.icon"}
          on:click={closeProperties}><span class="glyph-x" /></Button
        >
      </div>
      <div class="header">
        <span class="caption">Properties</span>
        <!--ThemeSwitch /-->
      </div>

      <div class="configurator general open">
        <ReportConfigurator
          bind:value={reportConfiguration}
          {fixedRangeType}
          locale={toLocale($globalLocale)}
        />
        <div class="configurator-controls">
          {#if feedTableProperty && Object.values(feedTableProperty.feeds).length > 1}
            <SelectField
              name="feed"
              label="Sensor"
              bind:value={selectedFeed}
              options={[
                {
                  value: "__all__",
                  label: `All ${sensorFeedType.title} sensors`,
                },
              ].concat(
                Object.values(feedTableProperty.feeds).map((feed) => {
                  return {
                    value: feed.id,
                    label: feed.properties.label,
                  };
                }),
              )}
            />
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  div.report {
    --report-caption-font-size: 24px;
    --report-header-info-font-size: 14px;
    --report-section-header-font-size: 18px;
    --report-chart-header-font-size: 16px;
    --report-footer-font-size: 14px;

    --base-color-bg-default: #2d2f36;
    --base-color-primary-default: #6bae85;
    --base-color-onSurface-secondary: #1a1b1f;
    --base-color-onSurface-primary: #dbe4e7;
    --core-color-crayon-honeyYellow: #fbc62f;
  }

  div.report {
    --surface-color: var(--base-color-onSurface-primary);
    --header-surface-color: var(--core-color-crayon-honeyYellow);
    --header-onSurface-color: var(--base-color-onSurface-secondary);
    --section-onSurface-color: var(--base-color-onSurface-secondary);
    --data-header-onSurface-color: var(--base-color-onSurface-primary);
    --scroll-color: var(--base-color-onSurface-secondary);
  }

  div.report.dark {
    --surface-color: var(--base-color-bg-default);
    --header-surface-color: var(--base-color-onSurface-secondary);
    --header-onSurface-color: var(--base-color-primary-default);
    --section-onSurface-color: var(--base-color-onSurface-primary);
    --data-header-onSurface-color: var(--base-color-bg-default);
    --scroll-color: var(--base-color-primary-default);
  }
  @media print {
    div.report.dark,
    div.report {
      --surface-color: transparent;
      --header-surface-color: transparent;
      --header-onSurface-color: var(--base-color-onSurface-secondary);
      --section-onSurface-color: var(--base-color-onSurface-secondary);
      --data-header-onSurface-color: white;
    }
  }

  .float {
    float: inline-end;
    position: relative;
    z-index: 10000;
  }
  div.properties {
    border-color: var(--section-onSurface-color);
  }
  div.properties.right {
    border-left: 1px solid var(--header-surface-color);
  }
  div.properties.right:not(.closed) {
    min-width: 425px;
  }
  div.properties.top {
    border-bottom: 1px solid var(--header-surface-color);
  }
  div.properties.closed {
    width: 0px;
    border: none;
  }
  div.properties.closed :global(*) {
    position: absolute;
    left: -9999px;
  }

  div.properties div.header {
    background-color: var(--header-surface-color);
    border-left: 2px solid var(--surface-color);
    padding: 1em;
    color: var(--section-onSurface-color);
  }
  div.properties div.header .caption {
    font-weight: 700;
    font-size: 18px;
    margin: 1em 0;
    display: inline-block;
  }

  div.properties .configurator {
    color: var(--section-onSurface-color);
  }

  div.properties div.configurator {
    display: none;
  }
  div.properties div.configurator.open {
    display: block;
  }

  div.properties.closed div.close {
    display: none;
  }
  div.properties div.configurator div.configurator-controls {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }

  div.report {
    background-color: var(--surface-color);
  }

  /*  .report table {
    border: 0;
    border-collapse: collapse;
    width: 100%;
  }
*/
  .report .content > header {
    background-color: var(--header-surface-color);
  }
  /* .report table thead {
    background-color: var(--header-surface-color);
  } */
  /*  .report table thead tr th {
    font-weight: normal;
    text-align: unset;
  }
*/
  div.report :global(.tlmd-domain-caption) {
    --domain-font-size: var(--report-caption-font-size);
  }

  .report .content > header,
  .report table thead tr th .header {
    color: var(--header-onSurface-color);
  }
  /*  table thead tr th {
    color: var(--header-onSurface-color);
  }
    */
  .report .content > header .caption,
  .report table thead tr th .header .caption {
    font-size: var(--report-caption-font-size);
    font-weight: bold;
  }
  .report .content > header .info,
  .report table thead tr th .header .info {
    font-size: var(--report-header-info-font-size);
    font-weight: bold;
    margin-top: 0.5em;
  }

  .report header.header {
    margin-bottom: 1rem;
  }

  /* .report table tfoot tr td .footer {
    font-size: var(--report-footer-font-size);
    margin-top: 0.5em;
  } */

  /* .report table tbody tr.section td {
    color: var(--section-onSurface-color);
  } */
  /* table tbody tr td {
    color: var(--section-onSurface-color);
  } */

  .report table tbody tr.section td .header {
    font-size: var(--report-section-header-font-size);
    font-weight: bold;
  }
  .report table tbody tr.section td .time-plot .header,
  .report table tbody tr.section td .chart .header {
    font-size: var(--report-chart-header-font-size);
    font-weight: bold;
  }

  /* .report table tfoot tr td {
    color: var(--section-onSurface-color);
  } */

  .report .content > header {
    padding: 1em;
  }
  /* .report table thead th {
    padding: 1em;
  } */
  /* .report table tfoot td {
    padding: 1em;
  } */
  /* .report table tbody td {
    padding: 0 1em 1em 1em;
  } */
  .report {
    font-family: Roboto;
  }

  div.statistics.pie {
    display: inline-block;
    min-width: 360px;
    width: calc(50% - 1em);
    max-width: 640px;
    margin: 0.5em;
    border: 1px solid var(--section-onSurface-color);
    border-radius: 0.5em;
    padding: 1em;
  }
  /* .report table tbody td :global(svg) {
    color: var(--section-onSurface-color);
  } */

  /* .report div.distribution {
		text-align: center;
	} */

  .time-plot .header {
    text-align: center;
    margin-top: 2rem;
  }
  @media screen {
    .report {
      display: grid;
    }

    .report {
      grid-template-columns: 1fr auto;
    }

    /* Styling the scrollbar in Firefox  */
    .tlmd-scroll {
      scrollbar-color: var(--scroll-color) transparent;
      scrollbar-width: thin;
    }
    /* Styling the scrollbar in Chrome and Safari */
    .tlmd-scroll::-webkit-scrollbar {
      width: 3px;
    }
    .tlmd-scroll::-webkit-scrollbar-thumb {
      background-color: var(
        --scroll-color
      ); /* Set thumb color for Chrome and Safari */
    }
    .tlmd-scroll::-webkit-scrollbar-track {
      background-color: transparent; /* Set track color for Chrome and Safari */
    }

    .report .content {
      grid-column: 1 / 2;
    }

    .report .properties {
      grid-column: 2 / 3;
    }

    .not-included {
      -webkit-filter: blur(5px) grayscale(1);
      -moz-filter: blur(5px) grayscale(1);
      -o-filter: blur(5px) grayscale(1);
      -ms-filter: blur(5px) grayscale(1);
      filter: blur(5px) grayscale(1);
    }
  }

  @media print {
    .report {
      display: block;
    }
    .report .content > header {
      display: none;
    }
    .not-included,
    .noprint {
      display: none;
      position: absolute;
      border: none;
      left: -9999px;
    }
    div.statistics.pie {
      min-width: 0;
    }
    div.properties.right.closed,
    div.properties.right {
      min-width: 0;
      width: 0;
    }
  }
</style>
